import {
  registerSchema,
  type RegisterDTO,
} from '@form-factory/schema/contracts';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  createFileRoute,
  Link,
  type SearchSchemaInput,
  useNavigate,
} from '@tanstack/react-router';
import { TRPCClientError } from '@trpc/client';
import { Button, toast } from '@utima/ui';
import type * as Form from '@utima/ui-informed';
import { useState } from 'react';

import { BasicForm } from '@/components/form/BasicForm';
import { SchemaFields } from '@/components/form/SchemaFields';
import { useFieldsSchema } from '@/components/form/useFieldsSchema';
import { trpc } from '@/services/trpc';

type RegistrationSearchParams = {
  organization: string;
  email: string;
  url: string;
};

export const Route = createFileRoute('/_authLayout/register/')({
  component: () => <RegisterPage />,
  // TODO: Change to zod (https://tanstack.com/router/v1/docs/framework/react/guide/search-params#zod)
  validateSearch: (
    search: {
      organization?: string;
      email?: string;
      url?: string;
    } & SearchSchemaInput,
  ): RegistrationSearchParams => {
    return {
      organization: (search.organization as string) ?? '',
      email: (search.email as string) ?? '',
      url: (search.url as string) ?? '',
    };
  },
});

export function RegisterPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { organization, email, url } = Route.useSearch();

  const registerMutation = trpc.auth.register.useMutation();
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = (formState: Form.TypedFormState<RegisterDTO>) => {
    setIsLoading(true);

    return registerMutation.mutate(
      { ...formState.values, url },
      {
        onError: async (error: unknown) => {
          if (error instanceof TRPCClientError) {
            const { data } = error;

            if (data.code === 'CONFLICT') {
              toast.error(t`User already exists`, {
                description: t`User with this email already exists.`,
              });
            }
          }
        },
        onSuccess: async () => {
          navigate({
            to: '/register/sent',
            search: { email: formState.values.email },
          });
        },
        onSettled: () => {
          setIsLoading(false);
        },
      },
    );
  };

  const { schema } = useFieldsSchema(
    [
      {
        name: 'organization',
        label: t`Organization name`,
        required: true,
        initialValue: organization,
        uiProps: {
          'data-cta': 'admin-register-organization-input',
        },
      },
      {
        name: 'email',
        label: t`Email`,
        required: true,
        initialValue: email,
        uiProps: {
          type: 'email',
          'data-cta': 'admin-register-email-input',
        },
      },
    ],
    [t],
  );

  return (
    <BasicForm
      onSubmit={onSubmit}
      zodSchema={registerSchema}
      className='flex w-full flex-col gap-6'
    >
      <h1 className='text-center text-2xl font-bold'>
        <Trans>Create your account</Trans>
      </h1>

      <SchemaFields schema={schema} />

      <Button
        variant='primary'
        size='lg'
        type='submit'
        data-cta='admin-register-submit-button'
        disabled={isLoading}
        loading={isLoading}
      >
        <Trans>Create your account</Trans>
      </Button>
      <div className='flex flex-col items-center justify-center gap-3'>
        <Link
          to='/login'
          data-cta='admin-register-login-link'
          className='text-sm hover:text-primary hover:underline'
        >
          <Trans>Already have an account? Login</Trans>
        </Link>
      </div>
    </BasicForm>
  );
}
